import type { AspectRatio, StudioMode } from '../studio/domain/studioTypes'
import {
  compileVoxnelaTaskPrompt,
  resolveVoxnelaTaskStudioMode,
} from './voxnelaTaskPrompts'
import { VOXNELA_EXPANDED_TASKS } from './voxnelaExpandedTasks'
import {
  VOXNELA_SIGNATURE_PROMPT_DRAFTS,
  compileVoxnelaSignatureDraftPrompt,
} from './voxnelaSignaturePromptDrafts'

export type VoxnelaTaskCategory =
  | 'voxnela'
  | 'business'
  | 'product'
  | 'stylize'
  | 'creative'
  | 'time_traveler'
  | 'poster'
  | 'editorial'

export interface VoxnelaTaskItem {
  id: string
  category: VoxnelaTaskCategory
  title: string
  shortTitle: string
  emoji: string
  badge: string
  description: string
  suggestions: [string, string]
  sampleImageUrl: string
  aspectRatio: AspectRatio
  recommendedStyleId: string
  studioMode: StudioMode
  promptTemplate: string
  needsWebGrounding?: boolean
  searchTags?: string[]
}

export const VOXNELA_TASK_CATEGORIES: Array<{
  id: VoxnelaTaskCategory
  label: string
  emoji: string
  shortLabel: string
}> = [
  { id: 'voxnela', label: 'Voxnela', emoji: '◢', shortLabel: 'Voxnela' },
  { id: 'creative', label: 'Kreatif', emoji: '🎨', shortLabel: 'Kreatif' },
  { id: 'stylize', label: 'Stilize Et', emoji: '🪄', shortLabel: 'Stilize Et' },
  { id: 'poster', label: 'Afiş & Poster', emoji: '▣', shortLabel: 'Afiş & Poster' },
  { id: 'time_traveler', label: 'Zaman Yolcusu', emoji: '⌛', shortLabel: 'Zaman Yolcusu' },
  { id: 'editorial', label: 'Editoryal', emoji: '▤', shortLabel: 'Editoryal' },
  { id: 'business', label: 'İş & Rapor', emoji: '💼', shortLabel: 'İş & Rapor' },
  { id: 'product', label: 'Ürün & Reklam', emoji: '◉', shortLabel: 'Ürün & Reklam' },
]

type RawVoxnelaTaskItem = Omit<VoxnelaTaskItem, 'studioMode'>

const RAW_VOXNELA_56_TASKS: RawVoxnelaTaskItem[] = [
  // ==========================================
  // KATEGORİ 1: İŞ & RAPOR (14 GÖREV)
  // ==========================================
  {
    id: 'quarterly_report_visualizer',
    category: 'business',
    title: 'Son Çeyrek Raporunu Görselleştir',
    shortTitle: 'Son Çeyrek Raporu',
    emoji: '📊',
    badge: 'Finans & KPI',
    description: 'Şirketinizin veya projenizin çeyrek dönem finansal ve büyüme verilerini üst düzey yönetici infografiğine dönüştürür. Kesin rakamlar, net pasta/çubuk grafik kartları ve modern kurumsal mizanpaj uygular.',
    suggestions: [
      '📎 Çeyrek bilanço veya performans PDF dosyanı ataçla ekle.',
      '💡 Yönetim kurulu ve yatırımcı sunumlarında anahtar slayt olarak kullan.',
    ],
    sampleImageUrl: '/tasks/quarterly_report_visualizer.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    needsWebGrounding: true,
    promptTemplate: 'Son çeyrek performans raporu görselleştirmesi. Yönetici seviyesinde modern infografik mizanpajı: Net KPI gösterge kartları, çeyreklik büyüme çubukları, gelir dağılımı oranları ve kurumsal tipografi. Kesinlikle uydurma veri yok, net, okunaklı, profesyonel İsviçre grafik tasarım standardında.',
  },
  {
    id: 'meeting_notes_flowchart',
    category: 'business',
    title: 'Toplantı Notlarını Şematize Et',
    shortTitle: 'Toplantı Notları Şeması',
    emoji: '📋',
    badge: 'Süreç & Özet',
    description: 'Uzun toplantı tutanaklarını ve döküm metinlerini tek bakışta anlaşılan görsel aksiyon planına, karar kutularına ve aşamalı iş akış şemasına çevirir.',
    suggestions: [
      '📎 Toplantı notlarını veya Word dökümünü ataçla yükle.',
      '💡 Ekip içi haftalık takip ve sorumluluk dağılımında kullan.',
    ],
    sampleImageUrl: '/tasks/meeting_notes_flowchart.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Toplantı notlarının görsel süreç şemasına ve aksiyon haritasına dönüştürülmesi. Aşamalı karar kutuları, oklarla birbirine bağlı adımlar, sorumluluk etiketleri, minimalist ve son derece okunaklı kurumsal infografik şablonu.',
  },
  {
    id: 'presentation_to_poster',
    category: 'business',
    title: 'Sunumu Posterleştir',
    shortTitle: 'Sunumu Posterleştir',
    emoji: '📑',
    badge: 'Konferans & Pano',
    description: 'Çok sayfalı PowerPoint veya PDF sunum dosyanızı fuar, konferans veya ofis duvar panosu için tek sayfalık göz alıcı bir özet infografik postere dönüştürür.',
    suggestions: [
      '📎 Sunum dosyanı (PPTX veya PDF) ataç butonundan ekle.',
      '💡 Konferans poster oturumlarında veya ofis içi vizyon panosunda sergile.',
    ],
    sampleImageUrl: '/tasks/presentation_to_poster.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Sunum içeriğinin tek sayfalık akademik ve kurumsal poster formatına uyarlanması. Üstte çarpıcı başlık ve problem tanımı, ortada 3 ana bulgu sütunu ve veri görselleştirmeleri, altta sonuç ve gelecek vizyonu özeti. Baskı kalitesinde tipografi.',
  },
  {
    id: 'data_infographic_converter',
    category: 'business',
    title: 'Veriyi İnfografiğe Dönüştür',
    shortTitle: 'Veri İnfografiği',
    emoji: '📈',
    badge: 'İstatistik & Analiz',
    description: 'Ham istatistikleri, pazar yüzdelerini ve karmaşık metrikleri anlaşılır, estetik ve vektörel bir infografik görseline dönüştürür.',
    suggestions: [
      '📎 Rapor veya veri tablosu dökümanını ekle.',
      '💡 Blog yazılarına, e-bültenlere ve sosyal medya analizlerine ekle.',
    ],
    sampleImageUrl: '/tasks/data_infographic_converter.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    needsWebGrounding: true,
    promptTemplate: 'Modern analitik veri infografiği. Geometrik veri blokları, yüzde dilimleri, karşılaştırmalı istatistik çubukları, yüksek kontrastlı profesyonel renk paleti, sıfır halüsinasyon, net ve zarif veri mimarisi.',
  },
  {
    id: 'workflow_process_modeler',
    category: 'business',
    title: 'İş Akışını (Flowchart) Modelle',
    shortTitle: 'İş Akışı Şeması',
    emoji: '🔀',
    badge: 'Operasyon & Süreç',
    description: 'Müşteri yolculuğunu, satış hunisini (funnel) veya operasyonel üretim sürecini modern ve şık bir flowchart şemasına döker.',
    suggestions: [
      '💡 Süreç adımlarını ister yaz ister döküman olarak yükle.',
      '💡 Yeni ekip oryantasyonlarında ve müşteri sunumlarında kullan.',
    ],
    sampleImageUrl: '/tasks/workflow_process_modeler.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Adım adım iş akışı (flowchart) ve süreç haritası modellemesi. Başlangıçtan sonuca yönlendirici oklar, durum kontrol düğümleri, modern UI simgeleri, açık ve net süreç aşamaları, kurumsal renk dengesi.',
  },
  {
    id: 'strategic_roadmap_timeline',
    category: 'business',
    title: 'Stratejik Yol Haritası (Roadmap)',
    shortTitle: 'Stratejik Roadmap',
    emoji: '🎯',
    badge: 'Vizyon & Plan',
    description: 'Şirketinizin veya ürününüzün yıllık ve çeyreklik hedeflerini, kilometre taşlarını estetik bir zaman çizgisi (timeline) üzerinde görselleştirir.',
    suggestions: [
      '💡 Çeyrek hedeflerini ve ana teslimat tarihlerini gir.',
      '💡 Yatırımcı toplantılarında stratejik vizyon slaytı yap.',
    ],
    sampleImageUrl: '/tasks/strategic_roadmap_timeline.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'luxury_minimal',
    promptTemplate: 'Stratejik yıllık ürün yol haritası (roadmap) zaman çizgisi. Q1, Q2, Q3, Q4 dönemlerine ayrılmış kilometre taşları, tamamlanan ve planlanan hedeflerin renk kodlaması, fütüristik ve seçkin kurumsal tasarım.',
  },
  {
    id: 'business_home_blueprint_tr',
    category: 'business', title: 'Ev Planı Blueprint', shortTitle: 'Ev Planı Blueprint', emoji: '⌂', badge: 'Mimari Plan',
    description: 'Oda yerleşimini, dolaşımı, kapı ve pencere konumlarını ölçülü bir ev planı blueprint’ine dönüştürür. Türkçe örnek görseli korunur.',
    suggestions: ['Oda listesini, arsa veya ev ölçülerini yaz ya da mevcut planı ekle.', 'Korunacak duvar, kapı, pencere ve yön bilgilerini belirt.'],
    sampleImageUrl: '/tasks/business-home-blueprint-tr.webp', aspectRatio: '16:9', recommendedStyleId: 'business_home_blueprint_tr',
    promptTemplate: 'Create a complete, elegant top-down residential floor-plan blueprint from the user brief or uploaded drawing. Show correctly connected rooms, wall thickness, doors with swing arcs, windows, circulation, furniture footprints, orientation and a restrained legend. Preserve every supplied measurement and structural constraint; when dimensions are missing, omit precise measurements rather than inventing them. Keep the plan conceptually plausible and label spaces clearly in the user’s selected language. Present it as a visual concept, not a permit-ready construction drawing.',
  },
  {
    id: 'business_architectural_blueprint',
    category: 'business', title: 'Mimari Blueprint', shortTitle: 'Mimari Blueprint', emoji: '⌂', badge: 'Plan & Kesit',
    description: 'Ev fikrini üstten plan ve aynı yapının izometrik kesitiyle sunan mimari blueprint hazırlar.',
    suggestions: ['Mekân programını, ölçüleri ve varsa mevcut çizimi ekle.', 'Malzeme, cephe, yön ve korunacak yapısal sınırları belirt.'],
    sampleImageUrl: '/tasks/business-architectural-blueprint.webp', aspectRatio: '16:9', recommendedStyleId: 'business_architectural_blueprint',
    promptTemplate: 'Create a premium architectural blueprint sheet with a coherent top-down home plan and a matching axonometric cutaway of the same structure. Include room program, door/window logic, circulation, furniture scale, orientation and a compact legend. Use dimensions only when supplied; never fake structural calculations, code compliance or construction approval. Preserve source-plan constraints and make the two views agree. Newly authored labels follow the user’s selected language.',
  },
  {
    id: 'business_computer_repair_blueprint',
    category: 'business', title: 'Bilgisayar Tamir Blueprint’i', shortTitle: 'Bilgisayar Blueprint’i', emoji: '🖥️', badge: 'Parça & Tamir',
    description: 'Bilgisayar bileşenlerini patlatılmış teknik görünümde, parça adları ve montaj ilişkileriyle görselleştirir.',
    suggestions: ['Cihaz fotoğrafını, modelini veya parça listesini ekle.', 'İncelenecek arızayı ve vurgulanacak parçaları belirt.'],
    sampleImageUrl: '/tasks/business-computer-repair-blueprint.webp', aspectRatio: '16:9', recommendedStyleId: 'business_computer_repair_blueprint',
    promptTemplate: 'Create a clear exploded-view repair blueprint for the exact computer or laptop described or shown in the supplied images. Identify visible or specified parts such as chassis, motherboard, cooling assembly, battery, storage, memory and display; use numbered callouts, a part key and plausible assembly direction. Do not invent a model-specific component, connector, screw count, diagnosis or part number. If the model is unknown, label the diagram as a generic conceptual layout. Newly authored labels follow the user’s selected language.',
  },
  {
    id: 'business_project_roadmap_blueprint',
    category: 'business', title: 'Proje Yol Haritası Blueprint’i', shortTitle: 'Proje Blueprint’i', emoji: '◇', badge: 'Teknik Yol Haritası',
    description: 'Proje aşamalarını, bağımlılıkları ve risk yollarını teknik blueprint diliyle gösterir.',
    suggestions: ['Aşamaları, çıktıları ve bağımlılıkları yaz veya proje belgesini ekle.', 'Varsa tarihleri, sorumluları ve riskleri belirt.'],
    sampleImageUrl: '/tasks/business-project-roadmap-blueprint.webp', aspectRatio: '16:9', recommendedStyleId: 'business_project_roadmap_blueprint',
    promptTemplate: 'Build an engineering-style project roadmap blueprint from the supplied brief or document. Show the project goal, ordered phases, deliverables, dependencies, decision gates, owners, a clearly distinguished risk or alternate path and a small status legend. Use exact dates and commitments only when provided; otherwise show relative sequence. Ensure every arrow has a meaningful direction and no phase is invented. Newly authored labels follow the user’s selected language.',
  },
  {
    id: 'business_visual_roadmap',
    category: 'business', title: 'Yol Haritası', shortTitle: 'Yol Haritası', emoji: '🧭', badge: 'Plan & Kilometre Taşı',
    description: 'Ürün veya iş planını çalışma kolları, dönemler, kilometre taşları ve teslimatlarla okunaklı bir yol haritasına çevirir.',
    suggestions: ['Hedefi, aşamaları ve tarihleri yaz veya plan belgesini ekle.', 'Öncelikleri, ekipleri ve tamamlanan işleri belirt.'],
    sampleImageUrl: '/tasks/business-visual-roadmap.webp', aspectRatio: '16:9', recommendedStyleId: 'business_visual_roadmap',
    promptTemplate: 'Create a presentation-ready visual roadmap from the user’s plan with a clear goal, time periods or relative stages, color-coded workstream lanes, milestone cards, owners if provided, dependencies, status legend and measurable outcomes when supplied. Distinguish committed work from ideas. Do not invent deadlines, progress, budgets or KPIs; omit or visibly mark missing fields. Newly authored labels follow the user’s selected language.',
  },
  {
    id: 'business_pest_analysis',
    category: 'business', title: 'PEST Analizi', shortTitle: 'PEST Analizi', emoji: '▦', badge: 'Dış Çevre Analizi',
    description: 'Politik, ekonomik, sosyal ve teknolojik dış etkenleri fırsat, risk ve stratejik etki olarak düzenler.',
    suggestions: ['Sektörü, pazarı ve ülkeyi belirt; varsa analiz notlarını ekle.', 'Doğrulanmış verileri ve kaynak tarihlerini paylaş.'],
    sampleImageUrl: '/tasks/business-pest-analysis.webp', aspectRatio: '16:9', recommendedStyleId: 'business_pest_analysis',
    promptTemplate: 'Create a complete PEST analysis page with four clearly labeled sections: Political, Economic, Social and Technological. For each factor, distinguish a source-grounded observation, its business implication, whether it is an opportunity or threat, and a practical response. Synthesize the most important cross-factor priorities at the bottom. Scope the analysis to the user’s supplied sector, market and time horizon. Do not invent laws, economic numbers, statistics, sources or forecasts. If source facts are insufficient, present a clearly labeled analysis framework with open questions. Newly authored labels follow the user’s selected language.',
  },
  {
    id: 'custom_slide_visual',
    category: 'business',
    title: 'Sunuma Özel Slayt Görseli',
    shortTitle: 'Slayt Görseli',
    emoji: '🖼️',
    badge: 'Sunum Tasarımı',
    description: 'Sunumunuzun konusuna, temasına ve kurumsal renklerine birebir uyan, stok görsel sıradanlığından uzak, yüksek etkili özel bir slayt ana görseli oluşturur.',
    suggestions: [
      '💡 Sunumunun başlığını veya konusunu yaz.',
      '💡 Keynote veya PowerPoint sunumuna tam sayfa arka plan yap.',
    ],
    sampleImageUrl: '/tasks/custom_slide_visual.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Kurumsal Keynote sunumuna özel tam sayfa ana görsel. Konsepti tek karede anlatan derin sinematik atmosfer, metin yerleşimi için dengeli negatif alan, birinci sınıf ışıklandırma ve seçkin görsel kalite.',
  },
  {
    id: 'annual_report_cover',
    category: 'business',
    title: 'Faaliyet Raporu Ön Kapağı',
    shortTitle: 'Faaliyet Raporu Kapağı',
    emoji: '🏢',
    badge: 'Prestij & Yayın',
    description: 'Şirketlerin yıllık faaliyet raporları, sürdürülebilirlik bildirgeleri ve kurumsal dökümanları için saygın ve prestijli bir ön kapak tasarımı yapar.',
    suggestions: [
      '💡 Şirketinin adını ve raporun yılını/temasını belirt.',
      '💡 PDF raporunun ilk sayfası veya basılı kapak olarak kullan.',
    ],
    sampleImageUrl: '/tasks/annual_report_cover.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'editorial_fashion',
    promptTemplate: 'Uluslararası kurumsal faaliyet raporu ön kapağı. Minimalist mimari ve soyut inovasyon görseli, prestijli serif ve sans-serif tipografi mizanpajı, kabartma baskı hissi veren lüks doku ve renk uyumu.',
  },
  {
    id: 'swot_comparison_matrix',
    category: 'business',
    title: 'SWOT Analizi',
    shortTitle: 'SWOT Analizi',
    emoji: '⚖️',
    badge: 'Strateji & Analiz',
    description: 'Güçlü ve zayıf yönleri, fırsatları ve tehditleri kanıta dayalı dört bölmeli bir strateji analizine dönüştürür.',
    suggestions: [
      '💡 Kurumu, ürünü veya projeyi ve analiz maddelerini yaz.',
      '💡 Strateji toplantılarında karar destek görseli olarak kullan.',
    ],
    sampleImageUrl: '/tasks/business-swot-analysis.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: '4 bölmeli profesyonel SWOT analiz matrisi (Güçlü Yönler, Zayıf Yönler, Fırsatlar, Tehditler). Her çeyrekte özel ikonlar, modern kart yapısı, renk kodlu hiyerarşi ve okunaklı madde düzeni.',
  },
  {
    id: 'pitch_deck_hero_visual',
    category: 'business',
    title: 'Pitch Deck Vizyon Görseli',
    shortTitle: 'Pitch Deck Vizyonu',
    emoji: '💡',
    badge: 'Girişim & Yatırım',
    description: 'Yatırımcı sunumunda fikrin gelecekteki potansiyelini ve vizyonunu tek bir vurucu karede anlatan fütüristik bir Hero sahnesi üretir.',
    suggestions: [
      '💡 Girişiminin ne yaptığını ve vizyonunu kısaca tarif et.',
      '💡 Yatırımcı sunumunun 1. slaytında dikkat çekmek için kullan.',
    ],
    sampleImageUrl: '/tasks/pitch_deck_hero_visual.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Start-up yatırımcı sunumu için dönüştürücü Hero sahnesi. İleri teknoloji, insan ve yapay zeka sinerjisini yansıtan aydınlık fütüristik atmosfer, umut verici ve ölçeklenebilir vizyon hissi, sinematik derinlik.',
  },
  {
    id: 'brainstorming_mindmap',
    category: 'business',
    title: 'Beyin Fırtınası Zihin Haritası',
    shortTitle: 'Zihin Haritası (Mindmap)',
    emoji: '🧠',
    badge: 'Fikir & İnovasyon',
    description: 'Karmaşık fikirleri, ürün özelliklerini ve alt başlıkları merkezden çevreye yayılan estetik bir zihin haritası (mindmap) olarak modeller.',
    suggestions: [
      '💡 Ana konunu ve alt başlıklarını yaz.',
      '💡 Tasarım ve ürün geliştirme çalıştaylarında kullan.',
    ],
    sampleImageUrl: '/tasks/brainstorming_mindmap.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Görsel zihin haritası (mindmap) ve kavram ağı. Merkezde parlayan ana fikir çekirdeği, düğümlerle ayrılan 5 ana daldaki alt konular, şık bağlantı hatları, minimalist ve renk kodlu beyin fırtınası haritası.',
  },
  {
    id: 'org_hierarchy_chart',
    category: 'business',
    title: 'Organizasyon & Hiyerarşi Şeması',
    shortTitle: 'Organizasyon Şeması',
    emoji: '🏗️',
    badge: 'Ekip & Yönetim',
    description: 'Şirketinizin departman yapısını, rollerini ve ekip hiyerarşisini kurumsal kimlikle uyumlu estetik bir şemaya döker.',
    suggestions: [
      '💡 Departmanları ve kilit rolleri gir.',
      '💡 Şirket içi el kitaplarında ve tanıtımlarda sergile.',
    ],
    sampleImageUrl: '/tasks/org_hierarchy_chart.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Modern kurumsal organizasyon ve ekip yapısı hiyerarşi şeması. Üst yönetimden ekiplere uzanan zarif bağlantılar, her pozisyon için şık kart tasarımı, profesyonel kurumsal renk paleti.',
  },
  {
    id: 'project_gantt_summary',
    category: 'business',
    title: 'Proje Planı & Gantt Özeti',
    shortTitle: 'Proje Planı / Gantt',
    emoji: '📋',
    badge: 'Planlama & Teslimat',
    description: 'Büyük bir projenin fazlarını, teslimat tarihlerini ve kilometre taşlarını renkli ve okunaklı bir proje panosuna dönüştürür.',
    suggestions: [
      '💡 Faz isimlerini ve tahmini haftaları/ayları belirt.',
      '💡 Müşteriye sunulacak proje teklif dosyasında yer ver.',
    ],
    sampleImageUrl: '/tasks/project_gantt_summary.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Proje teslim fazları ve Gantt zaman çizelgesi görselleştirmesi. Yatay zaman dilimleri, renk kodlu görev çubukları, teslim kilometre taşları, modern proje yönetim panosu tasarımı.',
  },
  {
    id: 'market_positioning_matrix',
    category: 'business',
    title: 'Pazar & Rakip Konumlandırma',
    shortTitle: 'Pazar Konumlandırması',
    emoji: '🌐',
    badge: 'Rekabet Analizi',
    description: 'Sektördeki oyuncuların ve markanızın konumunu x/y eksenli (fiyat/kalite, hız/kapsam vb.) görsel strateji grafiğine döker.',
    suggestions: [
      '💡 Eksen kriterlerini ve rakipleri yaz.',
      '💡 İş planı ve pazar analizi raporlarında kullan.',
    ],
    sampleImageUrl: '/tasks/market_positioning_matrix.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'luxury_minimal',
    needsWebGrounding: true,
    promptTemplate: 'X ve Y eksenli 2x2 pazar konumlandırma strateji grafiği. Markaların pazar payını ve segmentasyonunu gösteren şık küreler, net eksen etiketleri, üst segmentte vurgulanan ana marka konumu.',
  },

  // ==========================================
  // KATEGORİ 2: ÜRÜN & REKLAM (14 GÖREV)
  // ==========================================
  {
    id: 'studio_product_shoot',
    category: 'product',
    title: 'Stüdyo Ürün Çekimi',
    shortTitle: 'Stüdyo Ürün Çekimi',
    emoji: '📸',
    badge: 'E-Ticaret & Katalog',
    description: 'Yüklenen ürün fotoğrafını lüks stüdyo ışığı, yumuşak gölgeler ve temiz pedestal üzerine yerleştirerek üst segment bir katalog karesi üretir.',
    suggestions: [
      '📎 Ürün fotoğrafını ataç butonuyla yükle.',
      '💡 E-ticaret sitende, Amazon veya Shopify vitrininde sergile.',
    ],
    sampleImageUrl: '/tasks/studio_product_shoot.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'luxury_minimal',
    promptTemplate: 'Lüks ticari stüdyo ürün fotoğrafçılığı. Minimalist mermer kaide üzerinde sergilenen ürün, softbox yumuşak stüdyo aydınlatması, gerçekçi yansımalar ve temas gölgeleri, birinci sınıf ticari estetik.',
  },
  {
    id: 'launch_promo_poster',
    category: 'product',
    title: 'Lansman & Tanıtım Afişi',
    shortTitle: 'Lansman Afişi',
    emoji: '🏷️',
    badge: 'Kampanya & Lansman',
    description: 'Tipografi, slogan ve kampanya renkleriyle yeni ürün veya hizmetiniz için dikkat çeken modern lansman afişi tasarlar.',
    suggestions: [
      '💡 Ürün adını, sloganını ve ana özelliğini yaz.',
      '💡 Sosyal medyada duyuruda ve dijital reklamlarda kullan.',
    ],
    sampleImageUrl: '/tasks/launch_promo_poster.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'editorial_fashion',
    promptTemplate: 'Yüksek etkili ürün lansman ve tanıtım afişi. Cesur tipografi, modern renk blokları, merkezde parlayan ürün odağı, İsviçre grafik tasarım dengesi, merak uyandırıcı kampanya havası.',
  },
  {
    id: 'social_media_vertical_post',
    category: 'product',
    title: 'Instagram / TikTok Post Görseli',
    shortTitle: 'Instagram / TikTok Post',
    emoji: '📱',
    badge: 'Sosyal Medya',
    description: 'Sosyal medya algoritmasına ve kitle dikkatine uygun dinamik 9:16 dikey formatta tıklanma ve etkileşim odaklı görsel üretir.',
    suggestions: [
      '💡 Paylaşımın konusunu veya mesajını belirt.',
      '💡 Instagram Reels, Story veya TikTok kapak görseli yap.',
    ],
    sampleImageUrl: '/tasks/social_media_vertical_post.webp',
    aspectRatio: '9:16',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Sosyal medya için yüksek etkileşimli dikey 9:16 görsel. İlk 2 saniyede bakışları kilitleyen canlı kontrast, nefes kesici kompozisyon, sosyal trendlere uygun renk tonları.',
  },
  {
    id: 'lifestyle_product_context',
    category: 'product',
    title: 'Lifestyle (Kullanım Anı) Sahnesi',
    shortTitle: 'Lifestyle Ürün Çekimi',
    emoji: '☕',
    badge: 'Doğal Yaşam',
    description: 'Ürünü kafede, ofiste, sokakta veya evde kullanıcıyla doğal etkileşim halinde ve otantik bir yaşam ortamında sahneler.',
    suggestions: [
      '📎 Ürünün fotoğrafını ekle.',
      '💡 Sosyal medya reklamlarında samimi ve güven verici etki yarat.',
    ],
    sampleImageUrl: '/tasks/lifestyle_product_context.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'analog_film',
    promptTemplate: 'Doğal kullanım anı (lifestyle) ürün fotoğrafı. Sıcak güneş ışığı alan modern bir şehir kafesinde veya oturma odasında, doğal modellerle etkileşimde olan ürün, 35mm lens derinliği.',
  },
  {
    id: 'packaging_box_mockup',
    category: 'product',
    title: 'Kutu & Ambalaj Mockup\'ı',
    shortTitle: 'Ambalaj Mockup\'ı',
    emoji: '📦',
    badge: '3D Ambalaj',
    description: 'Ürün ambalajınızı, etiket tasarımınızı veya logonuzu gerçekçi 3D kutu, şişe veya paket üzerinde fotogerçekçi sahneler.',
    suggestions: [
      '📎 Varsa logo veya etiket görselini ataçla ekle.',
      '💡 Üretim öncesinde ambalajın rafta nasıl duracağını gör.',
    ],
    sampleImageUrl: '/tasks/packaging_box_mockup.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'luxury_minimal',
    promptTemplate: 'Gerçekçi 3D ürün ambalajı ve kutu mockup tasarımı. Mat dokulu premium karton kutu, zarif kabartma detaylar, profesyonel stüdyo ışığı ve temiz gölgeler.',
  },
  {
    id: 'discount_campaign_banner',
    category: 'product',
    title: 'İndirim & Sezon Kampanyası',
    shortTitle: 'İndirim Kampanyası',
    emoji: '📢',
    badge: 'Dönüşüm & Reklam',
    description: 'E-ticaret siteniz veya dijital reklamlar için yüksek dönüşüm sağlayan, indirim ve teklif odaklı şık kampanya banner\'ı hazırlar.',
    suggestions: [
      '💡 İndirim oranını (%50 vb.) ve kampanya adını yaz.',
      '💡 Web sitenin anasayfasında slider görseli olarak kullan.',
    ],
    sampleImageUrl: '/tasks/discount_campaign_banner.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'E-ticaret sezon indirim kampanyası banner tasarımı. Dinamik renk paleti, şık indirim vurguları, premium alışveriş atmosferi, dönüşüm artıran ticari görsel mizanpajı.',
  },
  {
    id: 'gourmet_food_menu',
    category: 'product',
    title: 'Gurme Yemek & Menü Çekimi',
    shortTitle: 'Gurme Yemek Çekimi',
    emoji: '🍔',
    badge: 'Restoran & Menü',
    description: 'Restoranlar, kafeler ve gıda markaları için iştah kabartan, dumanı üstünde gastronomik fotoğraf çekimi gerçekleştirir.',
    suggestions: [
      '💡 Yemeğin adını ve sunum tarzını tarif et.',
      '💡 Menüde, Yemeksepeti/Getir mağazasında veya Instagram\'da kullan.',
    ],
    sampleImageUrl: '/tasks/gourmet_food_menu.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Michelin yıldızlı gurme yemek fotoğrafçılığı. Taze malzemelerin canlı renkleri, sıcak ışık huzmeleri, mükemmel porsiyon sunumu ve iştah açıcı makro detaylar.',
  },
  {
    id: 'fashion_tryon_editorial',
    category: 'product',
    title: 'Moda & Giyim Try-On Çekimi',
    shortTitle: 'Moda & Giyim Çekimi',
    emoji: '👗',
    badge: 'Moda & Stil',
    description: 'Kıyafet veya ayakkabı tasarımınızı editoryal moda modeli üzerinde, kumaş dokusu ve dökümüyle doğal bir şekilde sahneler.',
    suggestions: [
      '📎 Kıyafetin fotoğrafını ataçla ekle.',
      '💡 Butiğin veya markanın yeni sezon lookbook\'unda kullan.',
    ],
    sampleImageUrl: '/tasks/fashion_tryon_editorial.webp',
    aspectRatio: '9:16',
    recommendedStyleId: 'editorial_fashion',
    promptTemplate: 'Yüksek moda editoryal çekimi. Profesyonel moda modeli üzerinde giysi dökümü, kumaş dokusu netliği, Paris moda haftası arka planı veya minimalist stüdyo fonu.',
  },
  {
    id: 'saas_product_hero_ui',
    category: 'product',
    title: 'SaaS & Dijital Ürün Hero Sahnesi',
    shortTitle: 'SaaS Hero Sahnesi',
    emoji: '🚀',
    badge: 'Web & Teknoloji',
    description: 'Web sitenizin veya uygulamanızın açılış sayfası için modern cam/neon efektli 3D arayüz ve teknoloji illüstrasyonu sahneler.',
    suggestions: [
      '💡 Dijital ürününün ne çözdüğünü kısaca yaz.',
      '💡 Web sitenin Hero (giriş) bölümünde başlığın arkasına koy.',
    ],
    sampleImageUrl: '/tasks/saas_product_hero_ui.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'SaaS ve dijital ürünler için modern Hero web görseli. Havada asılı duran yarı saydam cam UI kartları, veri akış çizgileri, modern degradeler ve teknoloji zarafeti.',
  },
  {
    id: 'testimonial_social_proof',
    category: 'product',
    title: 'Müşteri Yorumu (Testimonial)',
    shortTitle: 'Müşteri Referans Kartı',
    emoji: '🌟',
    badge: 'Sosyal Kanıt',
    description: 'Müşteri geri bildirimini estetik, güven veren ve kurumsal bir sosyal kanıt kartına dönüştürür.',
    suggestions: [
      '💡 Müşterinin yorumunu ve puanını gir.',
      '💡 Satış sayfasında güven unsuru olarak paylaş.',
    ],
    sampleImageUrl: '/tasks/testimonial_social_proof.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Güven veren müşteri referansı ve yorum kartı görseli. Şık tırnak işareti detayı, 5 yıldızlı derecelendirme ikonları, temiz arka plan ve prestijli tipografi.',
  },
  {
    id: 'event_conference_poster',
    category: 'product',
    title: 'Etkinlik & Konferans Posteri',
    shortTitle: 'Etkinlik Posteri',
    emoji: '🎪',
    badge: 'Zirve & Davetiye',
    description: 'Zirve, seminer, workshop veya lansman etkinliğiniz için baskıya hazır tipografik afiş tasarlar.',
    suggestions: [
      '💡 Etkinlik adını, tarihini ve temasını yaz.',
      '💡 Sosyal medyada duyur veya kapı girişine afiş bastır.',
    ],
    sampleImageUrl: '/tasks/event_conference_poster.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'editorial_fashion',
    promptTemplate: 'Uluslararası teknoloji zirvesi ve konferans afişi tasarımı. Büyük modern tipografi, geleceği simgeleyen soyut 3D şekiller, etkinlik detayları için dengeli mizanpaj.',
  },
  {
    id: 'marketplace_white_background',
    category: 'product',
    title: 'Amazon / Pazaryeri Beyaz Zemin',
    shortTitle: 'Pazaryeri Beyaz Zemin',
    emoji: '🏷️',
    badge: 'Amazon & E-Ticaret',
    description: 'Amazon, Trendyol ve Hepsiburada standartlarına uygun, kusursuz %100 saf beyaz dekupe ürün karesi çıkarır.',
    suggestions: [
      '📎 Ürün fotoğrafını yükle.',
      '💡 Pazaryeri ana listeleme görseli (Main Image) olarak yükle.',
    ],
    sampleImageUrl: '/tasks/marketplace_white_background.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Amazon ve e-ticaret pazaryerleri için kusursuz saf beyaz dekupe (#FFFFFF) ürün fotoğrafı. Sıfır gölge lekesi, mükemmel kenar netliği, ürünün orijinal renk doğruluğu.',
  },
  {
    id: 'youtube_high_ctr_thumbnail',
    category: 'product',
    title: 'YouTube Küçük Resmi (Thumbnail)',
    shortTitle: 'YouTube Thumbnail',
    emoji: '🎬',
    badge: 'Video & Tıklanma',
    description: 'YouTube tıklama oranını (CTR) katlayan, canlı renklere ve merak uyandıran kompozisyona sahip kapak karesi oluşturur.',
    suggestions: [
      '💡 Videonun konusunu ve merak unsurunu belirt.',
      '💡 YouTube veya video platformuna kapak görseli yap.',
    ],
    sampleImageUrl: '/tasks/youtube_high_ctr_thumbnail.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'YouTube tıklama rekoru kıran yüksek CTR thumbnail karesi. Güçlü yüz ifadesi veya çarpıcı nesne odağı, yüksek kontrast, canlı parlak renkler ve merak hissi.',
  },
  {
    id: 'retro_vintage_ad',
    category: 'product',
    title: 'Nostaljik Retro İlan Tasarımı',
    shortTitle: 'Retro İlan Tasarımı',
    emoji: '📻',
    badge: 'Vintage & Nostalji',
    description: '1970-80\'ler analog gazete veya dergi estetiğinde, nostaljik fontlar ve sararmış kağıt dokusuyla reklam tasarımı yapar.',
    suggestions: [
      '💡 Ürününü veya markanın nostaljik mesajını yaz.',
      '💡 Sosyal medyada yaratıcı ve viral bir kampanya için kullan.',
    ],
    sampleImageUrl: '/tasks/retro_vintage_ad.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'analog_film',
    promptTemplate: '1970\'ler New York dergisi vintage reklam ilanı. Noktalı matbaa baskı dokusu (halftone), nostaljik serif tipografi, sıcak sararmış kağıt arka planı ve nostaljik ticari illüstrasyon.',
  },

  // ==========================================
  // KATEGORİ 3: STİLİZE ET (14 GÖREV)
  // ==========================================
  {
    id: 'anime_manga_transform',
    category: 'stylize',
    title: 'Anime / Manga Karakteri Yap',
    shortTitle: 'Anime / Manga Dönüşümü',
    emoji: '🎌',
    badge: 'Japon Çizgi Roman',
    description: 'Yüklenen fotoğrafı modern Japon anime ve manga stiline dönüştürür; saç, göz ve kıyafet detaylarını koruyarak büyüleyici bir anime karakteri üretir.',
    suggestions: [
      '📎 Kendi portreni veya bir arkadaşının fotoğrafını ataçla ekle.',
      '💡 Sosyal medya profil resmi veya hediye poster yap.',
    ],
    sampleImageUrl: '/tasks/anime_manga_transform.webp',
    aspectRatio: '9:16',
    recommendedStyleId: 'anime_aesthetic',
    promptTemplate: 'Modern Shonen/Seinen anime tarzında sanatsal dönüşüm. Canlı parlak anime gözleri, stilize dinamik saç telleri, Makoto Shinkai tarzı gökyüzü ışığı ve detaylı manga estetiği.',
  },
  {
    id: 'pixar_3d_avatar',
    category: 'stylize',
    title: '3D Pixar / Animasyon Yap',
    shortTitle: '3D Pixar Karakteri',
    emoji: '🧸',
    badge: 'Sevimli 3D',
    description: 'Portrenizi sevimli, yuvarlak hatlı ve sıcak ışıklandırmalı bir Pixar/Disney 3D animasyon karakterine çevirir.',
    suggestions: [
      '📎 Yüzünün net göründüğü bir selfie ekle.',
      '💡 Animasyon filmi afişinde başrol gibi görün.',
    ],
    sampleImageUrl: '/tasks/pixar_3d_avatar.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Sevimli Pixar/Disney kalitesinde 3D animasyon karakteri. İri canlı gözler, hafif tebessüm, yumuşak cilt dokusu ve sıcak stüdyo ışığı, sevimli animasyon başrolü.',
  },
  {
    id: 'vintage_analog_35mm',
    category: 'stylize',
    title: 'Nostaljik 35mm Analog Film',
    shortTitle: '35mm Analog Film',
    emoji: '📸',
    badge: 'Kodak Portra',
    description: 'Işık sızıntıları, film greni ve otantik Kodak Portra 400 renk tonlarıyla fotoğrafınıza 90\'lar analog kamera hissi katar.',
    suggestions: [
      '📎 Herhangi bir anı veya manzara fotoğrafı ekle.',
      '💡 Nostaljik bir vintage hatıra tablosu oluştur.',
    ],
    sampleImageUrl: '/tasks/vintage_analog_35mm.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'analog_film',
    promptTemplate: 'Gerçekçi 35mm analog film estetiği. İnce gümüş halojen greni, hafif ışık sızıntıları (light leaks), nostaljik Kodak Portra sıcak renk tonları, vintage Leica lens hissi.',
  },
  {
    id: 'lego_minifig_brick',
    category: 'stylize',
    title: 'Lego / Mini Blok Figürü Yap',
    shortTitle: 'Lego / Blok Figürü',
    emoji: '🧱',
    badge: 'Oyuncak Dünyası',
    description: 'Sizi veya fotoğrafınızdaki sahneyi sevimli minyatür oyuncak yapı blokları (Lego) dünyasına taşır.',
    suggestions: [
      '📎 Bireysel veya arkadaş grubunun fotoğrafını yükle.',
      '💡 Eğlenceli bir avatar veya masaüstü duvar kağıdı yap.',
    ],
    sampleImageUrl: '/tasks/lego_minifig_brick.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Minyatür plastik oyuncak blok (Lego) figürü olarak sahneme. Sarı yuvarlak minifigür kafası, karakteristik plastik parçalar, parlak ABS plastik yüzey yansımaları ve minyatür makro çekim.',
  },
  {
    id: 'claymation_stop_motion',
    category: 'stylize',
    title: 'Kil Hamuru (Claymation) Yap',
    shortTitle: 'Kil Hamuru Sanatı',
    emoji: '🏺',
    badge: 'Stop-Motion',
    description: 'Fotoğrafı el yapımı, parmak izi detaylı sevimli bir stop-motion kil heykeline (Aardman stili) dönüştürür.',
    suggestions: [
      '📎 Portre veya evcil hayvan fotoğrafı ekle.',
      '💡 Benzersiz, sevimli ve sıcak bir sanat eseri oluştur.',
    ],
    sampleImageUrl: '/tasks/claymation_stop_motion.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'textured_zine',
    promptTemplate: 'El yapımı kil hamuru (claymation) stop-motion figürü. Yumuşak hamur dokusu, minik parmak izi detayları, Aardman animasyon filmi sıcaklığı ve minyatür stüdyo ışığı.',
  },
  {
    id: 'classic_oil_painting',
    category: 'stylize',
    title: 'Klasik Yağlı Boya Tablo Yap',
    shortTitle: 'Yağlı Boya Tablo',
    emoji: '🎨',
    badge: 'Rönesans Tuval',
    description: 'Fırça darbeleri, kanvas dokusu ve dramatik chiaroscuro ışığıyla fotoğrafınızı klasik bir müze tablosuna dönüştürür.',
    suggestions: [
      '📎 Portreni veya bir manzara fotoğrafını seç.',
      '💡 Kanvas tabloya bastırıp duvara asabileceğin bir eser yarat.',
    ],
    sampleImageUrl: '/tasks/classic_oil_painting.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'dark_cinematic',
    promptTemplate: 'Rönesans dönemi klasik yağlı boya tablosu. Belirgin fırça darbeleri, kanvas dokusu, Rembrandt tarzı dramatik ışık-gölge (chiaroscuro) ve zengin yağlı boya pigmentleri.',
  },
  {
    id: 'retro_16bit_pixel_art',
    category: 'stylize',
    title: 'Retro 16-Bit Pixel Art',
    shortTitle: '16-Bit Pixel Art',
    emoji: '👾',
    badge: 'Arcade Nostalji',
    description: 'Fotoğrafı 90\'ların efsanevi SNES ve arcade atari oyunları estetiğindeki rengarenk piksel sanatına uyarlar.',
    suggestions: [
      '📎 Portre veya şehir fotoğrafı yükle.',
      '💡 Oyun severler için nostaljik Discord veya Steam avatarı yap.',
    ],
    sampleImageUrl: '/tasks/retro_16bit_pixel_art.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: '16-bit nostaljik arcade piksel sanatı (pixel art). Özenle yerleştirilmiş pikseller, nostaljik video oyunu renk paleti, retro CRT monitör tarama çizgileri etkisi.',
  },
  {
    id: 'comic_book_pop_art',
    category: 'stylize',
    title: 'Çizgi Roman / Comic Book Yap',
    shortTitle: 'Çizgi Roman Stili',
    emoji: '🦸',
    badge: 'Pop-Art & Mürekkep',
    description: 'Mürekkep hatları, dramatik gölgeler ve pop-art Ben-Day noktacıklarıyla sizi bir çizgi roman kahramanına çevirir.',
    suggestions: [
      '📎 Dinamik poz verdiğin bir fotoğraf seç.',
      '💡 Marvel/DC kapağı hissi veren çarpıcı bir görsel elde et.',
    ],
    sampleImageUrl: '/tasks/comic_book_pop_art.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'editorial_fashion',
    promptTemplate: 'Klasik çizgi roman (comic book) kapağı estetiği. Kalın siyah mürekkep konturları, Ben-Day noktacık taramaları, dinamik gölgeler ve canlı pop-art renkleri.',
  },
  {
    id: 'cyberpunk_neon_avatar',
    category: 'stylize',
    title: 'Siberpunk / Neon Karakter Yap',
    shortTitle: 'Siberpunk Neon',
    emoji: '🕶️',
    badge: 'Gelecek & Neon',
    description: 'Neon ışıklar, sibernetik detaylar ve karanlık yağmurlu metropol havasıyla fotoğrafınızı fütüristik bir karaktere dönüştürür.',
    suggestions: [
      '📎 Portreni ekle ve siber çağa adım at.',
      '💡 Gelecek temalı fütüristik bir profil oluştur.',
    ],
    sampleImageUrl: '/tasks/cyberpunk_neon_avatar.webp',
    aspectRatio: '9:16',
    recommendedStyleId: 'cyberpunk',
    promptTemplate: 'Siberpunk 2077 estetiğinde neon portre. Yüzde ince sibernetik ışık hatları, neon mavi ve fuşya ışık yansımaları, ıslak gece metropolü arka planı ve yüksek teknoloji havası.',
  },
  {
    id: 'pet_royal_hero',
    category: 'stylize',
    title: 'Evcil Hayvanımı Kahraman Yap',
    shortTitle: 'Kahraman Evcil Hayvan',
    emoji: '🐾',
    badge: 'Kraliyet & Kahraman',
    description: 'Kedi veya köpeğinizi krallık pelerini giymiş asil bir hükümdara veya cesur bir uzay kahramanına dönüştürür.',
    suggestions: [
      '📎 Sevimli dostunun net bir fotoğrafını yükle.',
      '💡 Evin en özel köşesine asılacak komik ve asil bir tablo yap.',
    ],
    sampleImageUrl: '/tasks/pet_royal_hero.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'dark_cinematic',
    promptTemplate: '18. yüzyıl kraliyet portresinde asil evcil hayvan. Altın işlemeli kraliyet pelerini ve madalyaları giymiş asil kedi/köpek, yağlı boya saray atmosferi ve vakur duruş.',
  },
  {
    id: 'pencil_sketch_drawing',
    category: 'stylize',
    title: 'Karakalem Eskiz Çizimi Yap',
    shortTitle: 'Karakalem Eskiz',
    emoji: '✏️',
    badge: 'Karakalem Sanatı',
    description: 'Gerçekçi kurşun kalem gölgelendirmesi, taramalar ve dokulu kağıt etkisiyle el yapımı bir karakalem çizimi üretir.',
    suggestions: [
      '📎 Yüz veya mimari fotoğrafı yükle.',
      '💡 Sanat galerisi eskiz defteri sayfası hissi ver.',
    ],
    sampleImageUrl: '/tasks/pencil_sketch_drawing.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Usta sanatçı elinden çıkmış karakalem eskiz çizimi. Grafit kurşun kalem taramaları, hassas gölgelendirmeler, dokulu ağır resim kağıdı ve sanatsal serbest çizgiler.',
  },
  {
    id: 'dreamy_surreal_fantasy',
    category: 'stylize',
    title: 'Rüya & Sürreal Fantezi Yap',
    shortTitle: 'Rüya & Sürrealizm',
    emoji: '🔮',
    badge: 'Mistik & Rüya',
    description: 'Fotoğrafı yerçekimsiz, parıltılı ve masalsı bir boyuta taşıyarak büyüleyici bir sürreal sanat eseri oluşturur.',
    suggestions: [
      '📎 Portre veya doğa fotoğrafı ekle.',
      '💡 Sanatsal, mistik ve unutulmaz bir görsel dünya yarat.',
    ],
    sampleImageUrl: '/tasks/dreamy_surreal_fantasy.webp',
    aspectRatio: '9:16',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Sürrealist rüya alemi ve fantezi atmosferi. Havada süzülen parıltılı küreler, kristal yansımalar, masalsı yumuşak ışık ve yerçekimsiz büyüleyici görsel şiir.',
  },
  {
    id: 'antique_marble_statue',
    category: 'stylize',
    title: 'Antik Mermer Heykel Yap',
    shortTitle: 'Antik Mermer Heykel',
    emoji: '🗿',
    badge: 'Klasik Roma/Yunan',
    description: 'Portrenizi antik Roma veya Yunan tapınaklarındaki kusursuz klasik mermer büst heykeline dönüştürür.',
    suggestions: [
      '📎 Yüz hatlarının belirgin olduğu bir fotoğraf yükle.',
      '💡 Felsefi, asil ve zamansız bir sanat objesi elde et.',
    ],
    sampleImageUrl: '/tasks/antique_marble_statue.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'luxury_minimal',
    promptTemplate: 'Antik Yunan/Roma heykeltıraşlık şaheseri mermer büst. Beyaz damarlı Carrara mermeri dokusu, heykelsi zarif hatlar, loş müze ışığı ve asil antik duruş.',
  },
  {
    id: 'time_travel_vintage_era',
    category: 'stylize',
    title: 'Zamanda Yolculuk (80\'ler / 90\'lar)',
    shortTitle: 'Zamanda Yolculuk',
    emoji: '👶',
    badge: 'Dönem Portresi',
    description: 'Fotoğrafınızı 1980\'lerin kabarık saçlı okul yıllığına veya nostaljik bir dönem film karesine ışınlar.',
    suggestions: [
      '📎 Güncel bir fotoğrafını ekle.',
      '💡 Sosyal medyada eğlenceli "zamanda yolculuk" paylaşımı yap.',
    ],
    sampleImageUrl: '/tasks/time_travel_vintage_era.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'analog_film',
    promptTemplate: '1980\'ler nostaljik lise yıllığı fotoğrafı stili. Karakteristik retro saç modeli, döneme uygun kot ceket, lazerli retro stüdyo fonu ve nostaljik VHS renk tonlaması.',
  },

  // ==========================================
  // KATEGORİ 4: KREATİF & SANAT (14 GÖREV)
  // ==========================================
  {
    id: 'creative_photo_collage',
    category: 'creative',
    title: 'Kolaj',
    shortTitle: 'Kolaj',
    emoji: '🖼️',
    badge: 'Fotoğraf Kolajı',
    description: 'Yüklediğiniz fotoğrafları uyumlu renkler, dengeli yerleşim ve özgün kesimlerle tek bir yaratıcı kolajda birleştirir. Lütfen en az 2, en fazla 4 görsel ekleyin.',
    suggestions: [
      'Lütfen en az 2, en fazla 4 görsel ekleyin.',
      'İsterseniz kolajın temasını veya istediğiniz renkleri yazın.',
    ],
    sampleImageUrl: '/creation-style-samples/image/handmade-collage.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'creative_photo_collage',
    promptTemplate: 'Create one beautiful, cohesive photo collage using every uploaded image (2 to 4 images). Keep each source recognizable and its important people, objects, and details faithful. Compose an intentional editorial layout with varied image sizes, tasteful overlapping paper edges, subtle tactile texture, harmonious color grading, natural visual flow, and a clear focal point. Make the result feel personal and art directed, not a uniform grid or a random pile of cutouts. Do not invent extra people, replace faces, omit a source image, add gibberish text, or add watermarks. The uploaded images are the exact collage ingredients.',
  },
  {
    id: 'creative_memory_collage',
    category: 'creative',
    title: 'Anı Kolajı',
    shortTitle: 'Anı Kolajı',
    emoji: '📸',
    badge: 'Hatıra Kolajı',
    description: 'Fotoğraflarını sıcak, kişisel bir hatıra sayfasında birleştirir. Lütfen en az 2, en fazla 4 görsel ekleyin.',
    suggestions: [
      'Lütfen en az 2, en fazla 4 görsel ekleyin.',
      'İsterseniz anıların hikâyesini veya kullanmak istediğiniz renkleri yazın.',
    ],
    sampleImageUrl: '/tasks/creative-memory-collage.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'creative_memory_collage',
    promptTemplate: 'Create one premium memory collage from all 2 to 4 uploaded photographs. Preserve the real people, faces, places and important details in each source photo. Arrange the memories in a sophisticated editorial keepsake with varied photograph sizes, tactile paper edges, gentle archival texture, restrained handwritten accents and warm harmonious color grading. Tell a coherent visual story with a clear focal point while keeping every supplied image recognizable. Use dates, names and locations only if the user provided them. Do not invent people, change identities, omit a photo, add gibberish text or add watermarks.',
  },
  {
    id: 'creative_comic_story',
    category: 'creative',
    title: 'Çizgi Roman Hikâyesi',
    shortTitle: 'Çizgi Roman Hikâyesi',
    emoji: '💬',
    badge: 'Hikâye & Çizgi Roman',
    description: 'Kısa hikâyeni tutarlı karakterler, akıcı sahneler ve çizgi roman panelleriyle tek bir görsele dönüştürür.',
    suggestions: [
      'Kısa hikâyeni yaz; başlangıç, gelişme ve sonucu belirt.',
      'İstersen karakterlerin görünüşünü belirlemek için görsel ekle.',
    ],
    sampleImageUrl: '/tasks/creative-comic-story.png',
    aspectRatio: '9:16',
    recommendedStyleId: 'creative_comic_story',
    promptTemplate: 'Turn the user’s short story into one finished, vertical comic page with exactly four clearly separated panels. Adapt the story into a concise beginning, development, turning point and ending; retain its characters, relationships, setting and intended tone. Keep every recurring character visually consistent across all panels. Use expressive poses, cinematic framing, clear panel-to-panel continuity, polished comic illustration, refined ink work and controlled color. Add only brief dialogue or captions that advance the supplied story, written naturally in the user’s language; omit lettering when it would be unclear. Treat uploaded images as character or setting references and preserve their recognizable details. Do not invent a different plot, add extra panels, use English by default, create illegible lettering, or add watermarks.',
  },
  {
    id: 'cinematic_storyboard_frame',
    category: 'creative',
    title: 'Sinematik Storyboard Karesi',
    shortTitle: 'Sinematik Storyboard',
    emoji: '🎬',
    badge: 'Yönetmen & Sahne',
    description: 'Film, dizi veya reklam senaryosu için profesyonel sahne kompozisyonu, ışık ve kamera açısı karesi üretir.',
    suggestions: [
      '💡 Sahneyi, karakterin hareketini ve atmosferi tarif et.',
      '💡 Video çekim planında storyboard ve pitch hazırlığı yap.',
    ],
    sampleImageUrl: '/tasks/cinematic_storyboard_frame.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'cinematic',
    promptTemplate: '35mm anamorfik sinematik storyboard karesi. Hollywood prodüksiyon kalitesi, Roger Deakins tarzı atmosferik aydınlatma, sahne dramatik gerilimi ve kusursuz sinematik kadraj.',
  },
  {
    id: 'facelock_executive_portrait',
    category: 'creative',
    title: 'Face-Lock Profesyonel Portre',
    shortTitle: 'Face-Lock Portre',
    emoji: '👤',
    badge: 'Kimlik Koruma',
    description: 'Yüklenen fotoğraftaki yüz hatlarını ve mimikleri %100 koruyarak basın bülteni veya LinkedIn için üst düzey kurumsal stüdyo portresi çıkarır.',
    suggestions: [
      '📎 Kendi fotoğrafını ataç butonuyla yükle.',
      '💡 LinkedIn, şirket web sitesi veya konuşmacı tanıtımında kullan.',
    ],
    sampleImageUrl: '/tasks/facelock_executive_portrait.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'editorial_fashion',
    promptTemplate: '=== TASK: FACE-LOCK PRO PORTRAIT === Yüklenen referans fotoğraftaki kişinin yüz hatlarını, gözlerini, ten tonunu ve kimliğini %100 birebir koru. Seçkin koyu gri stüdyo fonu, şık takım elbise, profesyonel yönetici key lighting aydınlatması.',
  },
  {
    id: 'editorial_magazine_cover',
    category: 'creative',
    title: 'Editoryal Dergi Kapağı (Vogue / Time)',
    shortTitle: 'Editoryal Dergi Kapağı',
    emoji: '📖',
    badge: 'Vogue & Moda',
    description: 'Kişiyi veya konsepti prestijli dergi mizanpajı, üst logo tipografisi ve manşetlerle lüks dergi kapağına taşır.',
    suggestions: [
      '📎 Model veya portre fotoğrafını ekle.',
      '💡 Dergi kapağında yer alacak ana manşet fikrini yaz.',
    ],
    sampleImageUrl: '/tasks/editorial_magazine_cover.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'editorial_fashion',
    promptTemplate: 'Vogue/Time kalitesinde prestijli editoryal dergi kapağı. Üstte cesur masthead başlığı, yanlarda editoryal manşetler, merkezde büyüleyici model duruşu, lüks stüdyo aydınlatması.',
  },
  {
    id: 'brand_moodboard_palette',
    category: 'creative',
    title: 'Marka Moodboard & Renk Paleti',
    shortTitle: 'Marka Moodboard\'u',
    emoji: '🎨',
    badge: 'Tasarım & Palet',
    description: 'Yeni proje veya marka koleksiyonu için ilham verici görsel estetik, doku ve renk uyum panosu hazırlar.',
    suggestions: [
      '💡 Markanın hissini ve anahtar kelimelerini yaz.',
      '💡 Marka kimliği ve tasarım brifinglerinde pusula olarak kullan.',
    ],
    sampleImageUrl: '/tasks/brand_moodboard_palette.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'luxury_minimal',
    promptTemplate: 'Kreatif ajans marka ilham panosu (moodboard). 5 parçalı estetik kolaj: Doku örneği, mimari detay, renk paleti şeritleri, zarif tipografi ve marka ruhunu yansıtan ilham karesi.',
  },
  {
    id: 'interior_architectural_concept',
    category: 'creative',
    title: 'İç Mekân & Mimari Konsept',
    shortTitle: 'İç Mekân & Mimari',
    emoji: '🏙️',
    badge: 'Mimarlık & Dekor',
    description: 'Ofis, kafe, vitrin veya yaşam alanı için fotogerçekçi atmosfer ve mimari tasarım görselleştirmesi yapar.',
    suggestions: [
      '💡 Mekânın türünü ve dekorasyon tarzını belirt.',
      '💡 Müşteri sunumunda veya dekorasyon planında vizyon oluştur.',
    ],
    sampleImageUrl: '/tasks/interior_architectural_concept.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'luxury_minimal',
    promptTemplate: 'Architectural Digest tarzı modern iç mimari görselleştirme. Yüksek tavanlı ferah alan, sıcak meşe ahşap ve brüt beton uyumu, geniş pencerelerden süzülen doğal gün ışığı.',
  },
  {
    id: 'brand_3d_mascot_character',
    category: 'creative',
    title: '3D Marka Maskotu / Figür',
    shortTitle: '3D Marka Maskotu',
    emoji: '🕹️',
    badge: 'Karakter Tasarımı',
    description: 'Şirketinize veya kampanyanıza özel sevimli, ikonik ve akılda kalıcı 3D pop marka maskotu üretir.',
    suggestions: [
      '💡 Markanın sektörünü ve maskotun canlı/nesne türünü yaz.',
      '💡 Web sitende ve sosyal medyada marka yüzü olarak kullan.',
    ],
    sampleImageUrl: '/tasks/brand_3d_mascot_character.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Modern teknoloji markası için ikonik 3D maskot karakter tasarımı. Sevimli dost canlısı ifade, parlak mat yüzeyler, dinamik duruş, temiz stüdyo fonu.',
  },
  {
    id: 'futuristic_scifi_scene',
    category: 'creative',
    title: 'Fütüristik Bilim-Kurgu Sahnesi',
    shortTitle: 'Fütüristik Bilim Kurgu',
    emoji: '🌌',
    badge: 'Uzay & AI',
    description: 'Yapay zeka, uzay istasyonu ve yeni nesil teknoloji vizyonlarını nefes kesen detaylarla sahneler.',
    suggestions: [
      '💡 Hayal ettiğin bilim kurgu sahnesini yaz.',
      '💡 Teknolojik projelerde vizyoner bir görsel olarak kullan.',
    ],
    sampleImageUrl: '/tasks/futuristic_scifi_scene.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Epik bilim kurgu (sci-fi) uzay istasyonu konsepti. Yıldızlararası derin uzay manzarası, devasa dönen halka istasyon, parıldayan fütüristik şehir ışıkları, sinematik IMAX kadrajı.',
  },
  {
    id: 'sustainability_botanical_eco',
    category: 'creative',
    title: 'Sürdürülebilirlik & Eko-Vizyon',
    shortTitle: 'Sürdürülebilirlik & Doğa',
    emoji: '🌿',
    badge: 'Yeşil Enerji & Doğa',
    description: 'Çevre dostu, organik ve yeşil enerji mesajlarına uygun ferahlatıcı botanik sanat karesi üretir.',
    suggestions: [
      '💡 Sürdürülebilirlik projenin amacını belirt.',
      '💡 Kurumsal sürdürülebilirlik raporlarında yer ver.',
    ],
    sampleImageUrl: '/tasks/sustainability_botanical_eco.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Geleceğin yeşil sürdürülebilir eko-şehri. Ağaçlarla kaplı fütüristik dikey binalar, güneş panelleri, temiz akarsular ve yemyeşil doğayla bütünleşmiş umut dolu insan yaşamı.',
  },
  {
    id: 'stage_festival_atmosphere',
    category: 'creative',
    title: 'Sahne & Festival Atmosferi',
    shortTitle: 'Sahne & Festival',
    emoji: '🎭',
    badge: 'Işık & Gösteri',
    description: 'Konser, gala veya özel gece etkinliği için ışık şovlu, lazerli ve dumanlı dev sahne atmosferi tasarlar.',
    suggestions: [
      '💡 Etkinliğin türünü veya müzik türünü yaz.',
      '💡 Festival duyurularında ve bilet satışlarında kullan.',
    ],
    sampleImageUrl: '/tasks/stage_festival_atmosphere.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'cyberpunk',
    promptTemplate: 'Dev stadyum müzik festivali ana sahne atmosferi. Yüzlerce lazer ışığı, duman makineleri, sahneden yükselen alev efektleri ve coşkulu kalabalığın silueti.',
  },
  {
    id: 'book_cover_front_design',
    category: 'creative',
    title: 'Kitap / E-Kitap Ön Kapak Tasarımı',
    shortTitle: 'Kitap Kapak Tasarımı',
    emoji: '📚',
    badge: 'Yayın & Edebiyat',
    description: 'Roman, iş kitabı veya kişisel gelişim eseri için raflarda ve Kindle/Amazon vitrininde parlayan kapak kompozisyonu hazırlar.',
    suggestions: [
      '💡 Kitabının adını, yazarını ve türünü belirt.',
      '💡 Amazon KDP, e-kitap veya basılı yayın kapağı olarak kullan.',
    ],
    sampleImageUrl: '/tasks/book_cover_front_design.webp',
    aspectRatio: '4:5',
    recommendedStyleId: 'dark_cinematic',
    promptTemplate: 'Bestseller kitap ön kapak tasarımı. Merkezde güçlü sembolik ana görsel, üstte saygın yazar adı ve kitap başlığı tipografisi, ödüllü edebi eser estetiği.',
  },
  {
    id: 'music_album_cover_art',
    category: 'creative',
    title: 'Müzik Albüm / Single Kapağı',
    shortTitle: 'Albüm / Single Kapağı',
    emoji: '🎵',
    badge: 'Spotify & Apple Music',
    description: 'Şarkı veya albümünüz için Spotify ve Apple Music standartlarında sanatsal kare kapak oluşturur.',
    suggestions: [
      '💡 Şarkının adını ve müzik tarzını (RnB, Rock vb.) yaz.',
      '💡 Dijital müzik dağıtımında resmi parça kapağı yap.',
    ],
    sampleImageUrl: '/tasks/music_album_cover_art.webp',
    aspectRatio: '1:1',
    recommendedStyleId: 'cinematic',
    promptTemplate: 'Spotify resmi single müzik albüm kapağı. Derin sanatsal anlam taşıyan sürreal veya sinematik merkez obje, analog gren dokusu, modern bağımsız müzik estetiği.',
  },
  {
    id: 'minimal_vector_illustration',
    category: 'creative',
    title: 'Minimalist Vektör İllüstrasyon',
    shortTitle: 'Minimalist İllüstrasyon',
    emoji: '🖌️',
    badge: 'Flat Design',
    description: 'Modern web siteleri ve sunumlar için temiz çizgi, düz renk alanları ve çağdaş vektör illüstrasyon üretir.',
    suggestions: [
      '💡 İllüstre edilmesini istediğin durumu tarif et.',
      '💡 Web arayüzlerinde veya açıklayıcı grafiklerde kullan.',
    ],
    sampleImageUrl: '/tasks/minimal_vector_illustration.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'minimal_clean',
    promptTemplate: 'Çağdaş düz vektör illüstrasyon (flat vector design). Temiz geometrik hatlar, uyumlu pastel renk blokları, modern kurumsal teknoloji anlatımı, minimalist şıklık.',
  },
  {
    id: 'game_world_environment_concept',
    category: 'creative',
    title: 'Oyun Dünyası & Çevre Konsepti',
    shortTitle: 'Oyun Çevre Konsepti',
    emoji: '🏰',
    badge: 'Game Dev & Çevre',
    description: 'Oyun geliştiricileri ve dijital sanatçılar için geniş çevre, mekan ve atmosfer konsept sanatı (environment art) hazırlar.',
    suggestions: [
      '💡 Oyununun dünyasını (karanlık fantezi, post-apokaliptik vb.) belirt.',
      '💡 Oyun tasarım dokümanında (GDD) konsept görseli olarak kullan.',
    ],
    sampleImageUrl: '/tasks/game_world_environment_concept.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'dark_cinematic',
    promptTemplate: 'AAA oyun prodüksiyonu çevre konsept sanatı (environment concept art). Uçsuz bucaksız dağ silsileleri ortasında antik kale harabeleri, sisli atmosfer ve epik dünya inşası hissi.',
  },
  {
    id: 'historical_period_recreation',
    category: 'creative',
    title: 'Tarihi Sahne & Dönem Canlandırması',
    shortTitle: 'Tarihi Sahne Canlandırma',
    emoji: '🏛️',
    badge: 'Tarih & Belgesel',
    description: 'Antik uygarlıklar, Rönesans veya 1920\'ler atmosferini fotogerçekçi ve tarihsel detay doğruluğuyla yeniden canlandırır.',
    suggestions: [
      '💡 Hangi dönemi ve şehri canlandırmak istediğini yaz.',
      '💡 Belgesel, eğitim veya tarih merakı için benzersiz bir kare üret.',
    ],
    sampleImageUrl: '/tasks/historical_period_recreation.webp',
    aspectRatio: '16:9',
    recommendedStyleId: 'analog_film',
    needsWebGrounding: true,
    promptTemplate: 'Tarihsel doğrulukta dönem canlandırma fotoğrafı. 1920\'ler Paris veya 16. yüzyıl İstanbul sokakları, döneme uygun mimari ve kıyafetler, tarihi gerçeklik hissi ve belgesel derinliği.',
  },
]

export const VOXNELA_56_TASKS: VoxnelaTaskItem[] = RAW_VOXNELA_56_TASKS.map((task) => ({
  ...task,
  studioMode: resolveVoxnelaTaskStudioMode(task.id),
  promptTemplate: compileVoxnelaTaskPrompt(task),
}))

const SIGNATURE_SAMPLE_SLUGS: Partial<Record<string, string>> = {
  voxnela_year_in_one_frame: 'year-one-frame',
  voxnela_combin_analysis: 'combin-analysis',
  voxnela_next_years: 'next-years',
}

export const VOXNELA_SIGNATURE_TASKS: VoxnelaTaskItem[] = VOXNELA_SIGNATURE_PROMPT_DRAFTS.map((draft) => {
  const isAnalysis = draft.kind === 'analysis'
  const isNextYears = draft.id === 'voxnela_next_years'
  const isCombinAnalysis = draft.id === 'voxnela_combin_analysis'
  const isClothingTryOn = draft.id === 'voxnela_clothing_tryon'
  const signaturePrompt = compileVoxnelaSignatureDraftPrompt(draft.id) ?? draft.prompt
  const source: RawVoxnelaTaskItem = {
    id: draft.id,
    category: 'voxnela',
    title: draft.titleTr,
    shortTitle: draft.titleTr,
    emoji: '◢',
    badge: isAnalysis ? 'Voxnela Analiz' : 'Voxnela İmza',
    description: isClothingTryOn
      ? 'Tam boy fotoğrafını ve 1–4 kıyafet görselini ekle. Yapay zekâ kıyafetleri üzerinde dener; aksesuar, saç ve kullanım yeri önerileri sunar.'
      : isNextYears
      ? 'Fotoğrafından başlayarak 15, 30 ve 45 yıl sonraki hâlini tek görselde üç portre olarak gösterir. Önce yaşını sorar.'
      : isCombinAnalysis
        ? 'Mevcut kombinini ayrıntılı inceler; işe yarayan parçaları, düzeltmeleri ve üç uygulanabilir yeni kombin yönünü gösterir.'
        : isAnalysis
      ? 'Kaynak görseli koruyup gözlemleri, uygulanabilir önerileri ve yeniden tasarlanmış sonucu tek bir koleksiyonluk editoryal görselde birleştirir.'
      : 'Kişisel içeriği sıradan yapay zekâ estetiğinden uzak, güçlü sahne kurgusu ve özgün sanat yönetimiyle Voxnela imza görseline dönüştürür.',
    suggestions: isClothingTryOn
      ? ['Önce kendine ait net bir tam boy fotoğrafı yükle.', 'Ardından 1–4 kıyafet görseli ekle; gömlek, pantolon ve ayakkabıyı birlikte deneyebilirsin.']
      : isNextYears
      ? ['Yüzünün net göründüğü güncel bir fotoğrafını yükle.', 'Yaşını yaz; 15’er yıl arayla üç portreyi tek görselde oluşturalım.']
      : isCombinAnalysis
        ? ['Mevcut kombinini tam boy fotoğrafla yükle.', 'Nerede giyeceğini, mevsimi ve sevdiğin stili belirt.']
        : isAnalysis
      ? [
          'Kaynak fotoğrafı ve değiştirmek istediğin noktaları yükle.',
          'Önceliklerini, kullanım amacını ve korunması gereken ayrıntıları belirt.',
        ]
      : [
          'Kullanılacak fotoğrafları ve sahnenin hikâyesini yükle.',
          'Korunması gereken kişiler, yerler, tarihler ve ayrıntıları belirt.',
        ],
    sampleImageUrl: isClothingTryOn
      ? '/tasks/fashion_tryon_editorial.webp'
      : `/tasks/voxnela-signature/${SIGNATURE_SAMPLE_SLUGS[draft.id] ?? draft.id.replace(/^voxnela_/, '').replaceAll('_', '-')}.png`,
    aspectRatio: draft.aspectRatio,
    recommendedStyleId: isAnalysis || isNextYears ? draft.id : 'cinematic',
    needsWebGrounding: draft.requiresVerifiedData,
    promptTemplate: signaturePrompt,
  }

  return {
    ...source,
    studioMode: resolveVoxnelaTaskStudioMode(source.id),
    promptTemplate: compileVoxnelaTaskPrompt(source),
  }
})

export const VOXNELA_TASKS: VoxnelaTaskItem[] = [
  ...VOXNELA_56_TASKS,
  ...VOXNELA_EXPANDED_TASKS,
  ...VOXNELA_SIGNATURE_TASKS,
]

export function getTasksByCategory(category: VoxnelaTaskCategory): VoxnelaTaskItem[] {
  return VOXNELA_TASKS.filter((task) => task.category === category)
}

export function getTaskById(taskId: string): VoxnelaTaskItem | undefined {
  return VOXNELA_TASKS.find((task) => task.id === taskId)
}
